import React, { useState, useMemo } from 'react';
import { 
  Mail, Search, Star, ChevronDown, X, MessageSquare, 
  Briefcase, Shield, Building2, HeartPulse, Users, Filter
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { LETTER_TYPES } from '../../constants/mediscribe';

/**
 * LetterSelector - Improved letter type selection with search, favorites, and filters
 * Features:
 * - Type-to-search for letter types
 * - Favorites (starred letters appear first)
 * - Filter by category (Civilian, Military, Consult, etc.)
 * - 2-column layout for easier scanning
 * - "Other" always visible with custom description
 */
export const LetterSelector = ({
  letterType,
  setLetterType,
  setGenerateAdminLetter,
  customLetterDescription,
  setCustomLetterDescription,
  letterTone,
  setLetterTone,
  includePatientQuotes,
  setIncludePatientQuotes,
  consultRecipient,
  setConsultRecipient,
  consultQuestion,
  setConsultQuestion
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('writeright_letter_favorites') || '[]');
    } catch { return []; }
  });
  // Usage counts per letter-type value, persisted in localStorage. Used to
  // rank letter types after favorites so the most-used types bubble up.
  const [usageCounts, setUsageCounts] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('writeright_letter_usage') || '{}') || {};
    } catch { return {}; }
  });

  // Save favorites to localStorage
  const toggleFavorite = (value) => {
    const newFavorites = favorites.includes(value) 
      ? favorites.filter(f => f !== value)
      : [...favorites, value];
    setFavorites(newFavorites);
    localStorage.setItem('writeright_letter_favorites', JSON.stringify(newFavorites));
  };

  // Bump usage count for a letter type when selected (used for sort ranking)
  const bumpUsage = (value) => {
    setUsageCounts(prev => {
      const next = { ...prev, [value]: (prev[value] || 0) + 1 };
      try { localStorage.setItem('writeright_letter_usage', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  
  // Get unique categories
  const categories = useMemo(() => {
    const cats = [...new Set(LETTER_TYPES.filter(lt => lt.category).map(lt => lt.category))];
    return ['all', ...cats];
  }, []);
  
  // Filter and search letter types
  const filteredLetterTypes = useMemo(() => {
    let filtered = LETTER_TYPES.filter(lt => lt.value !== 'custom');
    
    // Apply category filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(lt => lt.category === activeFilter);
    }
    
    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(lt => 
        lt.label.toLowerCase().includes(query) ||
        (lt.category && lt.category.toLowerCase().includes(query))
      );
    }
    
    // Sort: favorites first → usage count (desc) → alphabetical.
    // This bubbles the most-used letter types to the top after starred ones,
    // matching the high-volume "speed charting" workflow without forcing the
    // provider to alphabetize from memory.
    return filtered.sort((a, b) => {
      const aFav = favorites.includes(a.value);
      const bFav = favorites.includes(b.value);
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      const aUse = usageCounts[a.value] || 0;
      const bUse = usageCounts[b.value] || 0;
      if (aUse !== bUse) return bUse - aUse;
      return a.label.localeCompare(b.label);
    });
  }, [searchQuery, activeFilter, favorites, usageCounts]);
  
  // Category icons
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Consult': return <Users className="w-3.5 h-3.5" />;
      case 'Return to Work': return <Briefcase className="w-3.5 h-3.5" />;
      case 'Civilian': return <Building2 className="w-3.5 h-3.5" />;
      case 'Workplace': return <Briefcase className="w-3.5 h-3.5" />;
      case 'Military': return <Shield className="w-3.5 h-3.5" />;
      default: return <Mail className="w-3.5 h-3.5" />;
    }
  };
  
  // Category colors
  const getCategoryColor = (category) => {
    switch (category) {
      case 'Consult': return 'text-cyan-500 bg-cyan-500/10 border-cyan-500/30';
      case 'Return to Work': return 'text-primary bg-primary/10 border-primary/30';
      case 'Civilian': return 'text-green-500 bg-green-500/10 border-green-500/30';
      case 'Workplace': return 'text-amber-500 bg-amber-500/10 border-amber-500/30';
      case 'Military': return 'text-blue-500 bg-blue-500/10 border-blue-500/30';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };
  
  const isConsultType = letterType?.startsWith('consult_');
  const selectedLetter = LETTER_TYPES.find(lt => lt.value === letterType);
  
  return (
    <div className="space-y-3">
      {/* Preferred Letter Style - shown first as a preference */}
      {setLetterTone && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Preferred Style:</Label>
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'brief', label: 'Brief', desc: '1-2 sentences' },
              { value: 'concise', label: 'Concise', desc: '1 paragraph' },
              { value: 'professional', label: 'Professional', desc: 'Standard' },
              { value: 'detailed', label: 'Detailed', desc: 'Full' },
              { value: 'mfr', label: 'MFR', desc: 'AR 25-50 Memorandum For Record' },
            ].map((tone) => (
              <button
                key={tone.value}
                onClick={() => setLetterTone(tone.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  letterTone === tone.value 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
                }`}
                data-testid={`letter-style-${tone.value}`}
                title={tone.desc}
              >
                {tone.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Selected Letter Display (if any) */}
      {letterType && (
        <div className="flex items-center gap-2 p-2 bg-primary/10 border border-primary/30 rounded-lg">
          <Mail className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium flex-1 truncate">
            {selectedLetter?.label || letterType}
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setLetterType('');
              setGenerateAdminLetter(false);
              if (setCustomLetterDescription) setCustomLetterDescription('');
            }} 
            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
      
      {/* Search Box */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search letter types..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 h-9 text-sm bg-input"
          data-testid="letter-search-input"
        />
      </div>
      
      {/* Category Filters */}
      <div className="flex gap-1.5 flex-wrap">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveFilter(cat)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
              activeFilter === cat 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            {cat === 'all' ? <Filter className="w-3 h-3" /> : getCategoryIcon(cat)}
            {cat === 'all' ? 'All' : cat}
          </button>
        ))}
      </div>
      
      {/* Letter Types Grid - 2 columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto pr-1">
        {/* "Other" option always visible at top */}
        <button
          onClick={() => {
            setLetterType('other');
            setGenerateAdminLetter(true);
          }}
          className={`flex items-center gap-2 p-2.5 rounded-lg text-left text-sm transition-all border ${
            letterType === 'other'
              ? 'bg-primary/20 border-primary text-foreground'
              : 'bg-card/50 border-border hover:bg-card hover:border-primary/50'
          }`}
        >
          <MessageSquare className="w-4 h-4 text-purple-500 flex-shrink-0" />
          <span className="flex-1">Other (custom)</span>
        </button>
        
        {/* Filtered letter types */}
        {filteredLetterTypes.map(lt => (
          <button
            key={lt.value}
            onClick={() => {
              setLetterType(lt.value);
              setGenerateAdminLetter(true);
              if (setCustomLetterDescription) setCustomLetterDescription('');
              bumpUsage(lt.value);
            }}
            className={`flex items-center gap-2 p-2.5 rounded-lg text-left text-sm transition-all border group ${
              letterType === lt.value
                ? 'bg-primary/20 border-primary text-foreground'
                : 'bg-card/50 border-border hover:bg-card hover:border-primary/50'
            }`}
          >
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(lt.value);
              }}
              className="flex-shrink-0 cursor-pointer"
            >
              <Star className={`w-4 h-4 transition-colors ${
                favorites.includes(lt.value) 
                  ? 'text-amber-400 fill-amber-400' 
                  : 'text-muted-foreground/30 group-hover:text-muted-foreground'
              }`} />
            </span>
            <span className="flex-1 truncate">{lt.label}</span>
            {lt.category && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${getCategoryColor(lt.category)}`}>
                {lt.category.slice(0, 3)}
              </span>
            )}
          </button>
        ))}
        
        {filteredLetterTypes.length === 0 && (
          <div className="col-span-2 text-center py-6 text-muted-foreground text-sm">
            No matching letter types found
          </div>
        )}
      </div>
      
      {/* Custom Description for "Other" */}
      {letterType === 'other' && setCustomLetterDescription && (
        <div className="space-y-2 pt-2 border-t border-border animate-in slide-in-from-top-2">
          <Label className="text-xs text-muted-foreground">What do you need?</Label>
          <Input
            placeholder="e.g., Immigration medical form, school physical, insurance appeal..."
            value={customLetterDescription || ''}
            onChange={(e) => setCustomLetterDescription(e.target.value)}
            className="h-9 text-sm bg-input"
            data-testid="custom-letter-description"
          />
        </div>
      )}
      
      {/* Consult-specific fields */}
      {isConsultType && (
        <div className="space-y-3 pt-2 border-t border-border animate-in slide-in-from-top-2">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="w-3 h-3" />
              Consult To:
            </Label>
            <Input
              placeholder="e.g., Dr. Smith, Behavioral Health, Clinical Pharmacist..."
              value={consultRecipient || ''}
              onChange={(e) => setConsultRecipient && setConsultRecipient(e.target.value)}
              className="h-9 text-sm bg-input"
              data-testid="consult-recipient"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />
              Question/Request:
            </Label>
            <Input
              placeholder="e.g., Medication change recommendation for insomnia..."
              value={consultQuestion || ''}
              onChange={(e) => setConsultQuestion && setConsultQuestion(e.target.value)}
              className="h-9 text-sm bg-input"
              data-testid="consult-question"
            />
          </div>
          <p className="text-[10px] text-muted-foreground/70">
            This will generate a brief, concise consult note with HPI summary + your question
          </p>
        </div>
      )}
      
      {/* Patient Quotes Option (for non-consult types) */}
      {letterType && !isConsultType && setIncludePatientQuotes !== undefined && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground flex items-center gap-1">
            <MessageSquare className="w-3 h-3" />
            Patient's Words:
          </Label>
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'none', label: 'None' },
              { value: 'summary', label: 'Summary' },
              { value: 'quotes', label: 'Direct Quotes' }
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setIncludePatientQuotes(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  includePatientQuotes === opt.value 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LetterSelector;
